import React from 'react';
import PropTypes from 'prop-types';
import { X, HelpCircle } from 'lucide-react';

/**
 * EnterpriseValueModal - Explains Enterprise Value and lists all valuation methods
 * Allows users to select a valuation method directly from the modal
 */
const EnterpriseValueModal = ({ isOpen, onClose, onSelectMethod, onStartQuiz }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-2xl font-bold text-gray-900">What is Enterprise Value (EV)?</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <X size={24} />
            </button>
          </div>

          <div className="prose prose-blue max-w-none">
            <p className="text-gray-700 mb-4">
              <strong>Enterprise Value (EV)</strong> is the total value of your business, representing what a buyer would pay to acquire 100% of your company. It's the most comprehensive measure of business value and is used across all our valuation methods.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">Available Valuation Methods</h3>
            <p className="text-gray-700 mb-4">
              Choose from five professional valuation methods, each designed for different business types and situations:
            </p>

            <ul className="space-y-3 mb-6">
              <li
                onClick={() => onSelectMethod('cca')}
                className="flex gap-3 p-3 rounded-lg cursor-pointer hover:bg-green-50 transition-colors border-2 border-transparent hover:border-green-200"
              >
                <span className="text-green-600 font-bold text-xl">📊</span>
                <div>
                  <strong className="text-gray-900">CCA (Comparable Company Analysis):</strong>
                  <p className="text-gray-700 text-sm mt-1">Values your business using industry multiples (EV/EBITDA and EV/Revenue) from 95+ industries with data from thousands of public companies. Best for established businesses with strong comparables.</p>
                </div>
              </li>
              <li
                onClick={() => onSelectMethod('dcf')}
                className="flex gap-3 p-3 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors border-2 border-transparent hover:border-blue-200"
              >
                <span className="text-blue-600 font-bold text-xl">💰</span>
                <div>
                  <strong className="text-gray-900">DCF (Discounted Cash Flow):</strong>
                  <p className="text-gray-700 text-sm mt-1">Values your business using two modes: Multi-period DCF (projects cash flows over a specified period with terminal value) or Gordon Growth Model (perpetuity valuation with constant growth). Ideal for businesses with predictable cash flows or consistent growth patterns.</p>
                </div>
              </li>
              <li
                onClick={() => onSelectMethod('capitalization')}
                className="flex gap-3 p-3 rounded-lg cursor-pointer hover:bg-purple-50 transition-colors border-2 border-transparent hover:border-purple-200"
              >
                <span className="text-purple-600 font-bold text-xl">💵</span>
                <div>
                  <strong className="text-gray-900">Capitalization of Earnings:</strong>
                  <p className="text-gray-700 text-sm mt-1">Divides annual earnings (EBITDA or Net Income) by a capitalization rate. Simple and effective for mature, stable businesses with consistent earnings.</p>
                </div>
              </li>
              <li
                onClick={() => onSelectMethod('nav')}
                className="flex gap-3 p-3 rounded-lg cursor-pointer hover:bg-orange-50 transition-colors border-2 border-transparent hover:border-orange-200"
              >
                <span className="text-orange-600 font-bold text-xl">🏢</span>
                <div>
                  <strong className="text-gray-900">NAV (Net Asset Value):</strong>
                  <p className="text-gray-700 text-sm mt-1">Calculates value based on total assets minus liabilities. Best suited for asset-heavy businesses like real estate or manufacturing.</p>
                </div>
              </li>
              <li
                onClick={() => onSelectMethod('manual')}
                className="flex gap-3 p-3 rounded-lg cursor-pointer hover:bg-indigo-50 transition-colors border-2 border-transparent hover:border-indigo-200"
              >
                <span className="text-indigo-600 font-bold text-xl">🎯</span>
                <div>
                  <strong className="text-gray-900">Manual Multiple:</strong>
                  <p className="text-gray-700 text-sm mt-1">Apply your own custom multiple from external appraisals or industry research. Choose from EV/EBITDA or EV/Revenue ratios with industry benchmarks shown for guidance.</p>
                </div>
              </li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">Private Company Discount</h3>
            <p className="text-gray-700 mb-4">
              All methods apply an adjustable <strong>private company discount</strong> (default 25%) to account for illiquidity. Private company shares are harder to sell than public shares, so buyers expect a discount. You can adjust this from 0-50% based on your business circumstances.
            </p>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-4 mt-6">
              <div className="flex items-start gap-3">
                <HelpCircle className="text-blue-600 mt-0.5" size={20} />
                <div>
                  <p className="text-sm font-semibold text-blue-900 mb-2">
                    Not sure which method to use?
                  </p>
                  <p className="text-sm text-blue-800 mb-3">
                    Take our quick quiz to find the best valuation method for your business type, stage, and available data.
                  </p>
                  <button
                    onClick={onStartQuiz}
                    className="bg-blue-600 text-white text-sm py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium min-h-[44px]"
                  >
                    Take the Quiz →
                  </button>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            className="mt-6 w-full bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors min-h-[44px]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

EnterpriseValueModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectMethod: PropTypes.func.isRequired,
  onStartQuiz: PropTypes.func.isRequired
};

export default EnterpriseValueModal;
